import { Box, Skeleton, Stack } from '@mui/material';

import { useSearchPageHeight } from './use-search-page-height';

export interface SearchPageSkeletonProps {
  /** Draw the title bar. Off for pages that put a PageTitle above instead. */
  hasHeader?: boolean;
  /** Draw the tab strip under the search bar. */
  hasTabs?: boolean;
  /** How many placeholder rows to draw. */
  rows?: number;
}

// Cell widths for one placeholder row, uneven so the rows read as a table of
// records rather than as stripes.
const SKELETON_CELL_WIDTHS = ['22%', '16%', '14%', '18%', '10%'];

/**
 * Loading state for a `SearchPage`, laid out like the real thing: title bar,
 * search bar, tabs, rows and footer, in a panel the same size. For the time
 * before a SearchPage can mount at all — its chunk or the data it needs to
 * build its query is still loading. Once mounted, SearchPage shows its own
 * loading rows.
 * @param root0 - Props.
 * @param root0.hasHeader - Whether to draw the title bar.
 * @param root0.hasTabs - Whether to draw the tab strip.
 * @param root0.rows - Number of placeholder rows.
 * @returns The skeleton panel.
 */
export function SearchPageSkeleton({
  hasHeader = true,
  hasTabs = false,
  rows = 10,
}: SearchPageSkeletonProps) {
  const height = useSearchPageHeight();

  return (
    <Box
      aria-busy
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Same box as SearchPage's own container.
        height,
        mt: 1,
        bgcolor: 'background.paper',
        borderRadius: { xs: 0, md: 2 },
        overflow: 'hidden',
      }}
    >
      {hasHeader && (
        <Stack
          // Stacked on a phone, like the real header: title, then the actions
          // on their own line.
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={{ xs: 1.5, sm: 2 }}
          sx={{
            flexShrink: 0,
            px: { xs: 2, md: 3 },
            py: 2,
            bgcolor: 'action.hover',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{ flex: { sm: 1 }, minWidth: 0, width: { xs: 1, sm: 'auto' } }}
          >
            <Skeleton width={180} height={28} />
            <Skeleton width="45%" height={20} />
          </Box>
          <Skeleton variant="rounded" width={96} height={36} />
        </Stack>
      )}

      {/* Search bar: one row at every width. Below md the sort dropdown lives
          in the filter panel, so only the search box and the icon buttons
          show. */}
      <Stack
        direction="row"
        spacing={{ xs: 0.5, md: 1.5 }}
        alignItems="center"
        sx={{
          flexShrink: 0,
          px: 1,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
        <Stack
          direction="row"
          spacing={{ xs: 0.75, md: 1 }}
          alignItems="center"
          flexShrink={0}
          // The real icon buttons are 34px with 3px of padding around a 28px
          // glyph; this keeps the search box the same width as the real one.
          sx={{ px: { xs: 0.375, md: 0 } }}
        >
          <Skeleton
            variant="rounded"
            width={140}
            height={40}
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Stack>
      </Stack>

      {hasTabs && (
        <Stack
          direction="row"
          spacing={3}
          sx={{
            flexShrink: 0,
            px: 2,
            height: 48,
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Skeleton width={60} height={24} />
          <Skeleton width={90} height={24} />
          <Skeleton width={75} height={24} />
        </Stack>
      )}

      {/* Rows */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {Array.from({ length: rows }, (_, row) => (
          <Stack
            key={row}
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              height: 52,
              px: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {SKELETON_CELL_WIDTHS.map((width, cell) => (
              <Skeleton
                key={width}
                width={width}
                height={22}
                // A phone has room for about three columns.
                sx={{
                  display: { xs: cell < 3 ? 'block' : 'none', md: 'block' },
                }}
              />
            ))}
          </Stack>
        ))}
      </Box>

      {/* Footer: count + pagination */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Skeleton width={110} height={22} />
        <Skeleton variant="rounded" width={160} height={32} />
      </Stack>
    </Box>
  );
}
